import { useCallback, useState } from 'react';
import { FormsV1 } from '@frontend/api-digital-forms';
import { http } from '@frontend/fetch';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../constants';

type VerifyOTPResult = {
  success: boolean;
  data?: Pick<
    FormsV1.Types.GetFormTemplateIO['output'],
    'personDetails' | 'medicalHistory' | 'primaryInsurance' | 'secondaryInsurance' | 'prePopulateElements'
  >;
  isServerError?: boolean;
};

export const useVerifyOTP = (qwe: string, companyId: string) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const fetchFormTemplate = FormsV1.Queries.useFetchGetFormTemplate();

  const verifyOTP = useCallback(
    async (otp: string): Promise<VerifyOTPResult> => {
      try {
        setIsVerifying(true);
        const res = await fetchFormTemplate({
          request: {
            secretCode: otp,
            companyId,
            qwe,
            includeOptions: true,
          },
          httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
        });

        // Legacy fetchFormData stripped personDetails for already-submitted forms, so they never pre-filled.
        const success = !res.isSubmitted && !!res.data && !!res.personDetails;

        return {
          success,
          data: {
            personDetails: res.personDetails,
            medicalHistory: res.medicalHistory,
            primaryInsurance: res.primaryInsurance,
            secondaryInsurance: res.secondaryInsurance,
            prePopulateElements: res.prePopulateElements,
          },
        };
      } catch (error) {
        const isServerError = http.isHttpError(error) && (error.response?.status ?? 400) >= 500;
        return {
          success: false,
          isServerError,
        };
      } finally {
        setIsVerifying(false);
      }
    },
    [fetchFormTemplate, companyId, qwe]
  );

  return { verifyOTP, isVerifying };
};
